"use client";

import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react/macro";
import { HistoryIcon } from "lucide-react";
import { type ReactNode, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { ChatListCard } from "../../_components/ai/chat-list/chat-list-card";
import { ChatHistoryToggleButtonProvider } from "../../_components/ai/thread/chat-history-toggle-context";

type Props = {
  children: ReactNode;
  initialIsHistoryOpen?: boolean;
};

const CHAT_HISTORY_OPEN_COOKIE = "amethyst_ai_chat_history_open";
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

function writeChatHistoryOpenCookie(isOpen: boolean): void {
  document.cookie = `${CHAT_HISTORY_OPEN_COOKIE}=${isOpen ? "true" : "false"}; path=/; max-age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax`;
}

export function AiChatViewShell({
  children,
  initialIsHistoryOpen = false,
}: Props) {
  const { i18n } = useLingui();
  const [isHistoryOpen, setIsHistoryOpen] = useState(initialIsHistoryOpen);
  const openHistoryLabel = i18n._(msg`Open chat history`);

  const setHistoryOpen = (isOpen: boolean) => {
    setIsHistoryOpen(isOpen);
    writeChatHistoryOpenCookie(isOpen);
  };

  const historyToggleButton = isHistoryOpen ? null : (
    <div className="hidden md:block">
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            size="icon-sm"
            variant="ghost"
            aria-label={openHistoryLabel}
            onClick={() => setHistoryOpen(true)}
          >
            <HistoryIcon className="h-4 w-4" />
            <span className="sr-only">
              <Trans>Open chat history</Trans>
            </span>
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          <Trans>Open chat history</Trans>
        </TooltipContent>
      </Tooltip>
    </div>
  );

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col">
      <div className="relative flex min-h-0 w-full flex-1 gap-0">
        {isHistoryOpen ? (
          <aside className="hidden min-h-0 w-[300px] shrink-0 flex-col border-r border-border/70 md:flex">
            <ChatListCard onClose={() => setHistoryOpen(false)} />
          </aside>
        ) : null}

        <section
          className={cn(
            "flex min-h-0 flex-1 flex-col overflow-hidden",
            !isHistoryOpen && "min-w-0",
          )}
        >
          <div className="flex h-full min-h-0 flex-col overflow-hidden">
            <ChatHistoryToggleButtonProvider value={historyToggleButton}>
              {children}
            </ChatHistoryToggleButtonProvider>
          </div>
        </section>
      </div>
    </div>
  );
}
